// SHOULD MATCH IN FE AND API REPOS
import { z } from 'zod';

export type CreateApplication = z.infer<typeof CreateApplicationSchema>;

// const capitalizeIfUppercase = (str: string | undefined) =>
//   str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
// Capitalizes the first letter of each word, where words are separated by spaces, hyphens, or apostrophes
// (so "Smith-Jones" and "O'Brien" keep their internal capitalization instead of being lowercased).
export const capitalizeIfUppercase = (str: string | undefined) =>
  str ? str.toLowerCase().replace(/(^|[\s'-])([a-z])/g, (_, separator, letter) => separator + letter.toUpperCase()) : str;

export const lowercaseIfUppercase = (str: string | undefined) => (str ? str.toLowerCase() : str);

export const CreateApplicationSchema = z.object({
  // step #1
  firstName: z.string().min(2).max(50).transform(capitalizeIfUppercase),
  middleName: z.string().optional().transform(capitalizeIfUppercase),
  lastName: z.string().min(2).max(50).transform(capitalizeIfUppercase),
  suffix: z.string().optional(), // TODO: enum
  emailAddress: z.string().email().transform(lowercaseIfUppercase),
  maritalStatus: z.string(),
  birthdate: z.string(), //z.date(),
  primaryPhoneType: z.string(), // enum
  primaryPhoneNumber: z.string(),
  ssn: z.string(),

  // step #2
  residentialAddressLine1: z.string().transform(capitalizeIfUppercase),
  residentialAddressLine2: z.string().optional().transform(capitalizeIfUppercase),
  residentialCity: z.string().transform(capitalizeIfUppercase),
  residentialState: z.string(), // enum
  residentialZipCode: z.string(),
  yearsAtResidentialAddress: z.coerce.number().min(0).max(99),
  monthsAtResidentialAddress: z.coerce.number().min(0).max(11),
  statusAtResidentialAddress: z.string(),
  monthlyPayment: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)),

  previousAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  previousAddressLine2: z.string().optional().transform(capitalizeIfUppercase),
  previousCity: z.string().optional().transform(capitalizeIfUppercase),
  previousState: z.string().optional(), // enum
  previousZipCode: z.string().optional(),
  previousYearsAtAddress: z.coerce.number().min(0).max(99).optional(),
  previousMonthsAtAddress: z.coerce.number().min(0).max(11).optional(),

  isRegistrationAddress: z.coerce.boolean(),
  registrationAddress: z.string().optional(),
  isMailingAddress: z.coerce.boolean(),
  mailingAddress: z.string().optional(),

  // step #3
  isRetired: z.coerce.boolean(),
  retirementIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  retirementYears: z.coerce.number().min(0).max(99).optional(),
  retirementMonths: z.coerce.number().min(0).max(11).optional(),

  isEmployed: z.coerce.boolean(),
  employerName: z.string().optional().transform(capitalizeIfUppercase),
  employerPhoneNumber: z.string().optional(),
  occupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  grossMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  employerAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  employerCity: z.string().optional().transform(capitalizeIfUppercase),
  employerState: z.string().optional(),
  employerZipCode: z.string().optional(),
  employedYears: z.coerce.number().min(0).max(99).optional(),
  employedMonths: z.coerce.number().min(0).max(11).optional(),

  previousEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  previousOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  previousGrossMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  previousEmployedYears: z.coerce.number().min(0).max(99).optional(),
  previousEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  isAdditionalIncome: z.coerce.boolean(),
  additionalIncomeSource: z.string().optional(),
  additionalIncomeSourceOther: z.string().optional().transform(capitalizeIfUppercase),
  additionalMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),

  haveCoApplicant: z.coerce.boolean(),
  coapplicantFirstName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantMiddleName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantLastName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantSuffix: z.string().optional(),
  coapplicantPrimaryPhoneType: z.string().optional(),
  coapplicantPrimaryPhoneNumber: z.string().optional(),
  coapplicantEmailAddress: z.string().optional().transform(lowercaseIfUppercase),
  coapplicantMaritalStatus: z.string().optional(),
  coapplicantSsn: z.string().optional(),
  coapplicantBirthdate: z.string().optional(),
  coapplicantResidentialAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantResidentialCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantResidentialState: z.string().optional(),
  coapplicantResidentialZipCode: z.string().optional(),
  coapplicantYearsAtResidentialAddress: z.coerce.number().min(0).max(99).optional(),
  coapplicantMonthsAtResidentialAddress: z.coerce.number().min(0).max(11).optional(),
  coapplicantStatusAtResidentialAddress: z.string().optional(),
  coapplicantMonthlyPayment: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),

  coapplicantPreviousAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousState: z.string().optional(),
  coapplicantPreviousZipCode: z.string().optional(),
  coapplicantPreviousYearsAtAddress: z.coerce.number().min(0).max(99).optional(),
  coapplicantPreviousMonthsAtAddress: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsRetired: z.coerce.boolean(),
  coapplicantRetirementIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  coapplicantRetirementYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantRetirementMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsEmployed: z.coerce.boolean(),
  coapplicantEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantGrossMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  coapplicantEmployerAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantEmployerCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantEmployerState: z.string().optional(),
  coapplicantEmployerZipCode: z.string().optional(),
  coapplicantEmployerPhoneNumber: z.string().optional(),
  coapplicantEmployedYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantPreviousEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousGrossMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),
  coapplicantPreviousEmployedYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantPreviousEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsAdditionalIncome: z.coerce.boolean(),
  coapplicantAdditionalIncomeSource: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantAdditionalIncomeSourceOther: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantAdditionalMonthlyIncome: z
    .string()
    .transform((str) => parseInt(str.replace(/,/g, ''), 10))
    .optional(),

  // step #4
  sellerType: z.string(),
  sellerName: z.string().transform(capitalizeIfUppercase),
  sellerPhoneNumber: z.string().optional(),
  sellerContact: z.string().optional().transform(capitalizeIfUppercase),
  // collateralType / unitStatus are only used by TSF (not CCL)
  collateralType: z.string().optional().transform(capitalizeIfUppercase),
  unitStatus: z.string().optional(),
  vehicleYear: z.coerce.number(),
  vehicleMake: z.string().transform(capitalizeIfUppercase),
  vehicleModel: z.string().transform(capitalizeIfUppercase),
  vehicleSalesPrice: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)),
  vehicleDownPayment: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)),
  notes: z.string().optional(),
  forDailyDriver: z.coerce.boolean(),
  referralSource: z.string().transform(capitalizeIfUppercase),
  referralSourceOther: z.string().optional().transform(capitalizeIfUppercase),

  // step #5
  usCitizen: z.coerce.boolean(),
  usResident: z.coerce.boolean(),
  applicantAgree: z.coerce.boolean(),
  applicantSignature: z.string(),
  coApplicantAgree: z.coerce.boolean().optional(),
  coApplicantSignature: z.string().optional(),
  coApplicantUsCitizen: z.coerce.boolean().optional(),
  coApplicantUsResident: z.coerce.boolean().optional(),

  // dealer info
  dealerId: z.string().optional(),
  dealerLocation: z.string().optional(),
  dealerStock: z.string().optional(),
  dealerName: z.string().optional(),

  // Google
  token: z.string(),

  // Phone application fields
  createdBy: z.string().optional(),
  phoneNumberCallback: z.string().optional(),
  analystName: z.string().optional(),
});
